import { useEffect, useState } from "react";
import styles from "./styles.module.scss";
import { FC, Props } from "./typings";

import settingService from "api/settingsService";
import {
  ENotificationChannel,
  INotification,
  INotificationSettingsReponse,
} from "dto/Settings/INotificationSettingsReponse";
import Checkbox from "components/Checkbox";
import classNames from "classnames";
import { PushNotifications } from "lib/sw-helpers";
import Group from "../Group";
import { useTranslation } from "react-i18next";

const NotificationRow = ({
  notification,
  onChange,
}: {
  notification: INotification;
  onChange?: (notification: INotification) => void;
}) => {
  const [isEmailActive, setIsEmailActive] = useState<boolean>(
    (notification.ActiveChannels & ENotificationChannel.Email) !== 0
  );
  const [isPushActive, setIsPushActive] = useState<boolean>(
    (notification.ActiveChannels & ENotificationChannel.WebPush) !== 0
  );

  const updateEmail = (value: boolean) => {
    if (value) {
      notification.ActiveChannels =
        notification.ActiveChannels | ENotificationChannel.Email;
    } else {
      notification.ActiveChannels =
        notification.ActiveChannels & ~ENotificationChannel.Email;
    }
    setIsEmailActive(value);
    onChange(notification);
  };

  const updatePush = (value: boolean) => {
    if (value) {
      notification.ActiveChannels =
        notification.ActiveChannels | ENotificationChannel.WebPush;
    } else {
      notification.ActiveChannels =
        notification.ActiveChannels & ~ENotificationChannel.WebPush;
    }

    setIsPushActive(value);
    onChange(notification);
  };

  return (
    <div className={styles.row}>
      <div className={styles.label}>{notification.Label}</div>
      <div className={styles.c1}>
        <Checkbox
          disabled={notification.IsStatic}
          value={isEmailActive}
          onChange={(v) => updateEmail(v)}
        ></Checkbox>
      </div>
      <div className={styles.c2}>
        <Checkbox
          disabled={notification.IsStatic}
          value={isPushActive}
          onChange={(v) => updatePush(v)}
        ></Checkbox>
      </div>
    </div>
  );
};

const BasicTab: FC<Props> = () => {
  const { t } = useTranslation();
  const {
    data: notificationSettingsData,
    isSuccess: isNotificationSettingsLoaded,
  } = settingService.useGetNotificationSettings();
  const { mutate: updateNotificationSetting } =
    settingService.useSetNotification();

  const [pushActive, setPushActive] = useState<boolean>(false);

  const checkIfPushIsActive = () => {
    setPushActive(
      window.Notification && window.Notification.permission === "granted"
    );
  };

  const activetePush = () => {
    PushNotifications.subscribeForPushNotifications(() =>
      checkIfPushIsActive()
    );
    checkIfPushIsActive();
  };

  setInterval(() => {
    checkIfPushIsActive();
  }, 1000); // Check every 1 second

  const [notificationSettings, setNotificationSettings] =
    useState<INotificationSettingsReponse>();

  const [group, setGroup] =
    useState<Partial<Record<string, INotification[]>>>();

  useEffect(() => {
    if (isNotificationSettingsLoaded) {
      setNotificationSettings(notificationSettingsData.data);

      setGroup(
        notificationSettingsData.data.Notifications.reduce((acc, item) => {
          acc[item.Group] = acc[item.Group] || [];
          acc[item.Group].push(item);
          return acc;
        }, {})
      );
    }
  }, [notificationSettingsData]);

  const handleChange = (notification: INotification) => {
    updateNotificationSetting(
      { Id: notification.Id, ActiveChannel: notification.ActiveChannels },
      {
        onSuccess() {
          //var newNotifications = [...notificationSettings!.Notifications];
          const toUpdate = notificationSettings?.Notifications.find(
            (x) => x.Id == notification.Id
          );

          if (toUpdate) {
            toUpdate.ActiveChannels = notification.ActiveChannels;
          }
        },
        onError() {},
      }
    );
  };

  useEffect(() => {
    checkIfPushIsActive();
  }, []);

  return (
    <div className={styles.wrapper}>
      <div className={styles.group}>
        <div className={styles.header}>{t("notifications.title")}</div>
        <div className={styles.hint}>
          {t("settings.emailNotificationConfig")}
        </div>

        <div className={styles.row}>
          {t("settings.pushNotifications")}{" "}
          <div
            className={classNames(
              styles.verification,
              pushActive ? styles.verified : ""
            )}
          >
            {pushActive ? t("basicTab.active") : t("basicTab.inactive")}
          </div>
        </div>
        {!pushActive && (
          <div className={styles.hint}>
            {t("settings.browserPermissionsNote")}{" "}
            {!pushActive && (
              <a onClick={() => activetePush()}>{t("basicTab.activateNotifications")}</a>
            )}
          </div>
        )}
        <div className={styles.hdrRow}>
          <div className={styles.label}></div>
          <div className={styles.c1}>{t("basicTab.email")}</div>
          <div className={styles.c1}>{t("basicTab.push")}</div>
        </div>
        <div className={styles.notifications}>
          {group &&
            Object.entries(group).map(([groupName, groupItems]) => (
              <>
                <Group header={groupName} initialState={false} variant="section">
                  <>
                    {groupItems.map((i) => (
                      <NotificationRow
                        notification={i}
                        onChange={handleChange}
                        key={i.Id}
                      ></NotificationRow>
                    ))}
                  </>
                </Group>
              </>
            ))}
        </div>
      </div>
    </div>
  );
};

export default BasicTab;
